import { ReactElement, useMemo, useEffect } from "react";
import { Button } from "@mui/material";
import { parseISO, format, eachDayOfInterval } from "date-fns";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";

import { AvailableSlotsNew as Slot } from "../../utils/booking";
import { groupByDayTime } from "utils/services/available-checker";

import { useBookingContext } from "hooks/useBooking";

type RangeType = {
  start: string;
  end: string;
  auto: boolean;
  isLoading: boolean;
};

const Range = ({
  range,
  rawSlots,
  selectedDay,
  handleDateSelected,
  timeZone,
  maxDate,
  minimum,
  serviceId,
}: {
  range: RangeType;
  rawSlots: Slot[];
  selectedDay: string;
  maxDate: string | null;
  // eslint-disable-next-line no-unused-vars
  handleDateSelected: (day: string) => void;
  timeZone: string;
  minimum: number;
  serviceId: string;
}) => {
  const context = useBookingContext();

  const interval = eachDayOfInterval({
    start: utcToZonedTime(
      formatInTimeZone(range.start, "UTC", "yyyy-MM-dd HH:mm:ssXXX"),
      timeZone,
    ),
    end: utcToZonedTime(
      formatInTimeZone(range.end, "UTC", "yyyy-MM-dd HH:mm:ssXXX"),
      timeZone,
    ),
  });

  const memoed = useMemo(() => {
    return rawSlots;
  }, [rawSlots]);

  useEffect(() => {
    let empty = groupByDayTime(
      (memoed?.filter((e: Slot) => e.date === selectedDay) as Slot[]) ?? [],
      timeZone,
      minimum,
      serviceId,
      context.data.serviceInfo,
    ).isEmpty;

    if (empty) {
      for (let i = 0; i <= interval.length - 1; i++) {
        empty = groupByDayTime(
          (memoed?.filter(
            (e: Slot) => e.date === format(interval[i], "yyyy-MM-dd"),
          ) as Slot[]) ?? [],
          timeZone,
          interval[i] > new Date() ? 0 : minimum,
          serviceId,
          context.data.serviceInfo,
        ).isEmpty;
        if (!empty) {
          handleDateSelected(format(interval[i], "yyyy-MM-dd"));
          break;
        }
      }
    }
  }, [
    handleDateSelected,
    interval,
    minimum,
    memoed,
    selectedDay,
    timeZone,
    serviceId,
    context.data.serviceInfo,
  ]);

  const onDateSelected = (day: string) => {
    handleDateSelected(day);
  };

  let source: ReactElement[] = [];

  interval.map((newDay) => {
    let disabled = groupByDayTime(
      (memoed?.filter(
        (e: Slot) => e.date === format(newDay, "yyyy-MM-dd"),
      ) as Slot[]) ?? [],
      timeZone,
      minimum,
      serviceId,
      context.data.serviceInfo,
    ).isEmpty;

    if (maxDate && newDay > parseISO(maxDate)) disabled = true;

    source.push(
      <Button
        variant={disabled ? "outlined" : "contained"}
        key={`${newDay.getDate()}`}
        sx={{
          p: 1,
          m: 0.5,
          width: { xs: "2.6rem", sm: "3rem" },
          height: { xs: "2.6rem", sm: "3rem" },
          minWidth: { xs: "2.6rem", sm: "3rem" },
          minHeight: { xs: "2.6rem", sm: "3rem" },
          fontSize: "12px",
          fontFamily: "ABCDiaTypeRegular",
          border: 1,
          color:
            format(newDay, "yyyy-MM-dd") === selectedDay ? "#fff" : "#725E54",
          background: disabled ? "none" : "#fff",
          backgroundColor:
            format(newDay, "yyyy-MM-dd") === selectedDay ? "#98A481" : "none",
          boxShadow:
            format(newDay, "yyyy-MM-dd") === selectedDay
              ? "1px 1px 6px 0 rgb(0 0 0 / 5%)"
              : 0,
          "&:hover": {
            backgroundColor: "#98A481",
            border: "none",
            color: "#fff",
          },
        }}
        disabled={disabled}
        onClick={() => onDateSelected(format(newDay, "yyyy-MM-dd"))}
      >
        {/* {formatInTimeZone(newDay, timeZone, "d eee")} */}
        {format(newDay, "d eee")}
      </Button>,
    );
  });
  return <>{source}</>;
};

export default Range;
